import { useEffect, useId, useState } from "react";
import { Button, Checkbox, FormControlLabel, Tooltip } from "@mui/material";

import "./style.scss";
import {
    deleteDigestionPolicyTemplate,
    fetchDigestionPolicyTemplates,
    fetchExtractedMetadata,
    fetchKnowledgeBaseConfig,
    patchKnowledgeBaseConfig,
    promoteDigestionPolicyTemplate,
    proposeDigestionPolicy,
    saveDigestionPolicyTemplate
} from "../../api/admin/knowledge-base/endpoints-referrers";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import type {
    DigestionPolicyField,
    DigestionPolicyTemplate,
    ExtractedMetadataDocument
} from "../../common/interfaces/admin/digestion-policy.interface";
import { ModalTemplate } from "../modal-template/modal-template.comp";
import { ToastContainer } from "../toast/toast.comp";
import ConfirmationPopup from "../confirmation-popup/confirmation-popup.comp";

const FLAGS: { key: keyof DigestionPolicyField; label: string; hint: string }[] = [
    { key: "filterable", label: "Filter", hint: "Offered as a search filter" },
    {
        key: "facet",
        label: "Facet",
        hint: "Value counts shown in the corpus overview and browsable"
    },
    { key: "multiValue", label: "Multi", hint: "A document can carry several values" },
    {
        key: "embedPrefix",
        label: "Embed",
        hint: "Value joins every chunk embedding — identity fields only"
    },
    {
        key: "required",
        label: "Required",
        hint: "Extraction must produce a value (add a fallback for a safety net)"
    }
];

/**
 * Editing row = policy field + the raw allowed-values text as typed. Parsing
 * commas on every keystroke into a controlled value eats separators and
 * spaces mid-typing, so the raw string stays in row state and the parsed
 * array is derived beside it; the transient key is stripped on save.
 */
type EditableField = DigestionPolicyField & { allowedValuesText?: string };

const emptyField = (): EditableField => ({
    name: "",
    type: "string",
    definition: "",
    scope: "document"
});

/**
 * Shared digestion-policy editor (G3-6, D1): one component, opened from both
 * KB Search Configuration and Agents admin. Edits the per-KB metadata
 * declaration, offers templates and an LLM-drafted proposal, and hosts the
 * extracted-metadata review. The corpus maintenance operations (re-extract /
 * re-chunk / legacy re-digest) live in the Agents list Action menu — the
 * house list-action pattern (DESIGN_GUIDE.md §4). Styling follows
 * DESIGN_GUIDE.md.
 */
export default function DigestionPolicyEditor({
    companyId,
    knowledgeBaseUid,
    knowledgeBaseName,
    onClose
}: {
    companyId: number;
    knowledgeBaseUid: string;
    knowledgeBaseName: string;
    onClose: () => void;
}) {
    const { showToast } = ToastContainer.useContainer();
    const { isSuperAdmin } = FirebaseAuthContainer.useContainer();
    const fieldId = useId();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [fields, setFields] = useState<EditableField[]>([]);
    const [storedVersion, setStoredVersion] = useState<number | null>(null);
    const [templates, setTemplates] = useState<DigestionPolicyTemplate[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [review, setReview] = useState<ExtractedMetadataDocument[] | null>(null);
    // Template panels (digestion_policy_templates_design.md §5): "save" is the
    // save-as-template row, "manage" the delete/promote list; overwrite
    // conflicts (409) surface as confirmation popups that retry with the flag.
    const [templatePanel, setTemplatePanel] = useState<"save" | "manage" | null>(null);
    const [templateName, setTemplateName] = useState("");
    const [overwritePrompt, setOverwritePrompt] = useState<string | null>(null);
    const [confirmDeleteTemplate, setConfirmDeleteTemplate] =
        useState<DigestionPolicyTemplate | null>(null);
    const [confirmPromoteOverwrite, setConfirmPromoteOverwrite] =
        useState<DigestionPolicyTemplate | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const [cfgRes, tplRes] = await Promise.all([
                    fetchKnowledgeBaseConfig(companyId, knowledgeBaseUid),
                    fetchDigestionPolicyTemplates(companyId)
                ]);
                const policy = cfgRes.data?.digestionPolicy;
                setFields(policy?.fields ?? []);
                setStoredVersion(policy?.version ?? null);
                setTemplates(tplRes.data?.templates ?? []);
            } catch (e: any) {
                setError(e?.response?.data?.message ?? "Failed to load the digestion policy");
            } finally {
                setLoading(false);
            }
        })();
    }, [companyId, knowledgeBaseUid]);

    const updateField = (index: number, patch: Partial<EditableField>) => {
        setFields(prev => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
    };

    const save = async () => {
        setBusy("save");
        setError(null);
        try {
            const policyFields = fields.map(({ allowedValuesText, ...field }) => field);
            await patchKnowledgeBaseConfig(companyId, knowledgeBaseUid, {
                digestionPolicy: fields.length ? { fields: policyFields } : null
            });
            showToast("Digestion policy saved — manifest recompiled", 3000);
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? "Failed to save the digestion policy");
        } finally {
            setBusy(null);
        }
    };

    const propose = async () => {
        setBusy("propose");
        setError(null);
        setNotice(null);
        try {
            const res = await proposeDigestionPolicy(companyId, knowledgeBaseUid);
            const draft = res.data?.draft?.fields ?? [];
            const dropped = res.data?.dropped ?? [];
            setFields(draft);
            setNotice(
                `AI proposed ${draft.length} field${draft.length === 1 ? "" : "s"}` +
                    (dropped.length ? ` (${dropped.length} invalid candidate(s) dropped)` : "") +
                    " — review before saving."
            );
        } catch (e: any) {
            setError(e?.response?.data?.message ?? "Policy proposal failed");
        } finally {
            setBusy(null);
        }
    };

    const toggleReview = async () => {
        if (review) {
            setReview(null);
            return;
        }
        setBusy("review");
        setError(null);
        try {
            const res = await fetchExtractedMetadata(companyId, knowledgeBaseUid);
            setReview(res.data.documents ?? []);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? "Failed to load extracted metadata");
        } finally {
            setBusy(null);
        }
    };

    const templateLabel = (t: DigestionPolicyTemplate) =>
        t.scope === "system" ? `${t.name} (system)` : t.name;

    const applyTemplate = (uid: string) => {
        const tpl = templates.find(t => t.uid === uid);
        if (tpl) {
            setFields(tpl.fields);
            setNotice(`Template “${templateLabel(tpl)}” applied — review and save.`);
        }
    };

    const reloadTemplates = async () => {
        const res = await fetchDigestionPolicyTemplates(companyId);
        setTemplates(res.data?.templates ?? []);
    };

    // Saves the EDITOR's current fields (what you see is what you save), never
    // the last persisted policy; `version` is stripped with allowedValuesText.
    const saveTemplate = async (overwrite = false) => {
        const name = templateName.trim();
        if (!name) return;
        setBusy("saveTemplate");
        setError(null);
        try {
            const policyFields = fields.map(({ allowedValuesText, ...field }) => field);
            await saveDigestionPolicyTemplate(companyId, {
                name,
                fields: policyFields,
                ...(overwrite ? { overwrite: true } : {})
            });
            setTemplatePanel(null);
            setOverwritePrompt(null);
            setNotice(`Template “${name}” saved.`);
            await reloadTemplates();
        } catch (e: any) {
            if (e?.response?.status === 409 && !overwrite) {
                setOverwritePrompt(name);
            } else {
                setError(e?.response?.data?.message ?? "Failed to save the template");
            }
        } finally {
            setBusy(null);
        }
    };

    const removeTemplate = async (tpl: DigestionPolicyTemplate) => {
        setConfirmDeleteTemplate(null);
        setBusy("deleteTemplate");
        setError(null);
        try {
            await deleteDigestionPolicyTemplate(companyId, tpl.uid);
            setNotice(`Template “${templateLabel(tpl)}” deleted.`);
            await reloadTemplates();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? "Failed to delete the template");
        } finally {
            setBusy(null);
        }
    };

    const promoteTemplate = async (tpl: DigestionPolicyTemplate, overwrite = false) => {
        setBusy("promoteTemplate");
        setError(null);
        try {
            await promoteDigestionPolicyTemplate(companyId, tpl.uid, overwrite || undefined);
            setConfirmPromoteOverwrite(null);
            setNotice(`Template “${tpl.name}” copied to system.`);
            await reloadTemplates();
        } catch (e: any) {
            if (e?.response?.status === 409 && !overwrite) {
                setConfirmPromoteOverwrite(tpl);
            } else {
                setError(e?.response?.data?.message ?? "Failed to copy the template to system");
            }
        } finally {
            setBusy(null);
        }
    };

    const describeValues = (doc: ExtractedMetadataDocument) => {
        if (doc.kind === "legacy") return "legacy document — re-digest to extract";
        if (!doc.fields || !Object.keys(doc.fields).length) return "no values extracted";
        return Object.entries(doc.fields)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
            .join(" | ");
    };

    return (
        <ModalTemplate
            open
            width={1040}
            ariaLabel={`Digestion policy for ${knowledgeBaseName}`}
            handleClose={() => {
                if (!busy) onClose();
            }}
        >
            <div className="digestion-policy">
                <h2 className="digestion-policy__title">Digestion Policy</h2>
                <div className="digestion-policy__subtitle">{knowledgeBaseName}</div>
                <p className="digestion-policy__sub">
                    Declares the metadata fields extracted from every document in this Assistant.
                    Fields drive search filters, facets and embedding context. No fields means no
                    metadata — the pipeline runs fine without any.
                    {storedVersion !== null && ` Stored policy version: ${storedVersion}.`}
                </p>
                <div className="digestion-policy__toolbar">
                    <label
                        className="digestion-policy__toolbar-label"
                        htmlFor={`${fieldId}-template`}
                    >
                        Template:
                    </label>
                    <select
                        id={`${fieldId}-template`}
                        className="digestion-policy__select"
                        value=""
                        disabled={!!busy || loading}
                        onChange={e => applyTemplate(e.target.value)}
                    >
                        <option value="">Apply template…</option>
                        {templates.map(t => (
                            <option key={t.uid} value={t.uid} title={t.description ?? undefined}>
                                {templateLabel(t)}
                            </option>
                        ))}
                    </select>
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading || fields.length === 0}
                        onClick={() => {
                            setTemplatePanel(prev => (prev === "save" ? null : "save"));
                            setTemplateName(knowledgeBaseName);
                        }}
                    >
                        Save as template…
                    </Button>
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading || templates.length === 0}
                        onClick={() =>
                            setTemplatePanel(prev => (prev === "manage" ? null : "manage"))
                        }
                    >
                        {templatePanel === "manage" ? "Hide templates" : "Manage templates…"}
                    </Button>
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading}
                        onClick={propose}
                    >
                        {busy === "propose" ? "Proposing…" : "Propose with AI"}
                    </Button>
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading}
                        onClick={() => setFields(prev => [...prev, emptyField()])}
                    >
                        Add field
                    </Button>
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading || fields.length === 0}
                        onClick={() => setFields([])}
                    >
                        Clear all
                    </Button>
                </div>
                {templatePanel === "save" && (
                    <div className="digestion-policy__template-panel">
                        <div className="digestion-policy__field-group digestion-policy__field-group--name">
                            <label htmlFor={`${fieldId}-template-name`}>Template name</label>
                            <input
                                id={`${fieldId}-template-name`}
                                type="text"
                                value={templateName}
                                onChange={e => setTemplateName(e.target.value)}
                            />
                        </div>
                        <Button
                            className="digestion-policy__tool"
                            disabled={!!busy || !templateName.trim()}
                            onClick={() => saveTemplate()}
                        >
                            {busy === "saveTemplate" ? "Saving…" : "Save template"}
                        </Button>
                        <Button
                            className="digestion-policy__tool"
                            disabled={!!busy}
                            onClick={() => setTemplatePanel(null)}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
                {templatePanel === "manage" && (
                    <div className="digestion-policy__template-panel">
                        <table
                            className="digestion-policy__review-table"
                            aria-label="Digestion policy templates"
                        >
                            <thead>
                                <tr>
                                    <th>Template</th>
                                    <th>Scope</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates.map(t => (
                                    <tr key={t.uid}>
                                        <td>{t.name}</td>
                                        <td>{t.scope}</td>
                                        <td>
                                            {(t.scope === "tenant" || isSuperAdmin) && (
                                                <Button
                                                    className="digestion-policy__tool"
                                                    size="small"
                                                    disabled={!!busy}
                                                    onClick={() => setConfirmDeleteTemplate(t)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                            {t.scope === "tenant" && isSuperAdmin && (
                                                <Button
                                                    className="digestion-policy__tool"
                                                    size="small"
                                                    disabled={!!busy}
                                                    onClick={() => promoteTemplate(t)}
                                                >
                                                    {busy === "promoteTemplate"
                                                        ? "Copying…"
                                                        : "Copy to system"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {notice && (
                    <p className="digestion-policy__notice" role="status">
                        {notice}
                    </p>
                )}
                {error && (
                    <p className="digestion-policy__error" role="alert">
                        {error}
                    </p>
                )}
                <div className="digestion-policy__body">
                    {loading ? (
                        <p className="digestion-policy__empty">Loading…</p>
                    ) : fields.length === 0 ? (
                        <p className="digestion-policy__empty">
                            No metadata fields declared. Start from a template, let the AI propose a
                            draft, or add fields by hand.
                        </p>
                    ) : (
                        fields.map((f, i) => (
                            <div className="digestion-policy__field" key={i}>
                                <div className="digestion-policy__field-row">
                                    <div className="digestion-policy__field-group digestion-policy__field-group--name">
                                        <label htmlFor={`${fieldId}-name-${i}`}>
                                            Field name (snake_case)
                                        </label>
                                        <input
                                            id={`${fieldId}-name-${i}`}
                                            type="text"
                                            value={f.name}
                                            onChange={e => updateField(i, { name: e.target.value })}
                                        />
                                    </div>
                                    <div className="digestion-policy__field-group digestion-policy__field-group--type">
                                        <label htmlFor={`${fieldId}-type-${i}`}>Type</label>
                                        <select
                                            id={`${fieldId}-type-${i}`}
                                            className="digestion-policy__select"
                                            value={f.type}
                                            onChange={e => {
                                                const type = e.target
                                                    .value as DigestionPolicyField["type"];
                                                updateField(i, {
                                                    type,
                                                    ...(type !== "enum"
                                                        ? {
                                                              allowedValues: undefined,
                                                              allowedValuesText: undefined,
                                                              fallback: f.fallback
                                                          }
                                                        : {})
                                                });
                                            }}
                                        >
                                            <option value="string">string</option>
                                            <option value="enum">enum</option>
                                            <option value="date">date</option>
                                        </select>
                                    </div>
                                    <div className="digestion-policy__flags">
                                        {FLAGS.map(flag => (
                                            <Tooltip key={flag.key} title={flag.hint}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={!!f[flag.key]}
                                                            onChange={e =>
                                                                updateField(i, {
                                                                    [flag.key]:
                                                                        e.target.checked ||
                                                                        undefined
                                                                })
                                                            }
                                                        />
                                                    }
                                                    label={flag.label}
                                                />
                                            </Tooltip>
                                        ))}
                                    </div>
                                    <Button
                                        className="digestion-policy__remove"
                                        size="small"
                                        aria-label={`Remove field ${f.name || i + 1}`}
                                        onClick={() =>
                                            setFields(prev => prev.filter((_, j) => j !== i))
                                        }
                                    >
                                        Remove
                                    </Button>
                                </div>
                                <div className="digestion-policy__field-group digestion-policy__field-group--wide">
                                    <label htmlFor={`${fieldId}-definition-${i}`}>
                                        Extraction definition — what the value means and where to
                                        find it
                                    </label>
                                    <textarea
                                        id={`${fieldId}-definition-${i}`}
                                        rows={2}
                                        value={f.definition}
                                        onChange={e =>
                                            updateField(i, { definition: e.target.value })
                                        }
                                    />
                                </div>
                                {f.type === "enum" && (
                                    <div className="digestion-policy__field-row">
                                        <div className="digestion-policy__field-group digestion-policy__field-group--wide">
                                            <label htmlFor={`${fieldId}-values-${i}`}>
                                                Allowed values (comma-separated)
                                            </label>
                                            <input
                                                id={`${fieldId}-values-${i}`}
                                                type="text"
                                                value={
                                                    f.allowedValuesText ??
                                                    (f.allowedValues ?? []).join(", ")
                                                }
                                                onChange={e =>
                                                    updateField(i, {
                                                        allowedValuesText: e.target.value,
                                                        allowedValues: e.target.value
                                                            .split(",")
                                                            .map(v => v.trim())
                                                            .filter(Boolean)
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="digestion-policy__field-group">
                                            <label htmlFor={`${fieldId}-fallback-${i}`}>
                                                Fallback
                                            </label>
                                            <input
                                                id={`${fieldId}-fallback-${i}`}
                                                type="text"
                                                value={f.fallback ?? ""}
                                                onChange={e =>
                                                    updateField(i, {
                                                        fallback: e.target.value || undefined
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                    {review && (
                        <div className="digestion-policy__review">
                            <div
                                className="digestion-policy__review-title"
                                role="heading"
                                aria-level={3}
                            >
                                Extracted metadata by document
                            </div>
                            <table
                                className="digestion-policy__review-table"
                                aria-label="Extracted metadata by document"
                            >
                                <thead>
                                    <tr>
                                        <th>Document</th>
                                        <th>Kind</th>
                                        <th>Policy</th>
                                        <th>Extracted values</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {review.map(doc => (
                                        <tr key={doc.uid}>
                                            <td>{doc.fileName ?? doc.uid}</td>
                                            <td>{doc.kind === "pdf_v2" ? "pdf" : doc.kind}</td>
                                            <td>{doc.policyVersion ?? "—"}</td>
                                            <td>{describeValues(doc)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="digestion-policy__actions">
                    <Button
                        className="digestion-policy__tool"
                        disabled={!!busy || loading}
                        aria-expanded={!!review}
                        onClick={toggleReview}
                    >
                        {busy === "review"
                            ? "Working…"
                            : review
                              ? "Hide metadata review"
                              : "Review extracted metadata"}
                    </Button>
                    <div className="digestion-policy__actions-right">
                        <Button
                            className="digestion-policy__cancel"
                            disabled={busy === "save"}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="digestion-policy__submit"
                            disabled={!!busy || loading}
                            onClick={save}
                        >
                            {busy === "save" ? "Saving…" : "Save policy"}
                        </Button>
                    </div>
                </div>
            </div>
            <ConfirmationPopup
                open={!!overwritePrompt}
                title="Overwrite template"
                description={`A template named “${overwritePrompt ?? ""}” already exists. Overwrite it with the current fields?`}
                actionName="Overwrite"
                handleAction={() => saveTemplate(true)}
                handleClose={() => setOverwritePrompt(null)}
            />
            <ConfirmationPopup
                open={!!confirmDeleteTemplate}
                title="Delete template"
                description={`Delete the template “${
                    confirmDeleteTemplate ? templateLabel(confirmDeleteTemplate) : ""
                }”? Policies already saved from it are not affected.`}
                actionName="Delete"
                handleAction={() => confirmDeleteTemplate && removeTemplate(confirmDeleteTemplate)}
                handleClose={() => setConfirmDeleteTemplate(null)}
            />
            <ConfirmationPopup
                open={!!confirmPromoteOverwrite}
                title="Overwrite system template"
                description={`A system template named “${
                    confirmPromoteOverwrite?.name ?? ""
                }” already exists. Overwrite it with this tenant template?`}
                actionName="Overwrite"
                handleAction={() =>
                    confirmPromoteOverwrite && promoteTemplate(confirmPromoteOverwrite, true)
                }
                handleClose={() => setConfirmPromoteOverwrite(null)}
            />
        </ModalTemplate>
    );
}
